import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AnimatePresence } from "framer-motion";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import LocationsPage from "./pages/LocationsPage";
import DistributorsPage from "./pages/DistributorsPage";
import ContactPage from "./pages/ContactPage";
import CalculatorPage from "./pages/CalculatorPage";
import DealerFormPage from "./pages/DealerFormPage";
import MediaPage from "./pages/MediaPage";
import BlogListPage from "./pages/BlogListPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import StrengthScannerPage from "./pages/StrengthScannerPage";
import SavingsCalculatorPage from "./pages/SavingsCalculatorPage";
import ToolsHubPage from "./pages/ToolsHubPage";
import DealerEarningsPage from "./pages/DealerEarningsPage";
import DealerStatusPage from "./pages/DealerStatusPage";
import ComparisonPage from "./pages/ComparisonPage";
import BulkOrderPage from "./pages/BulkOrderPage";
import FAQPage from "./pages/FAQPage";
import LabReportsPage from "./pages/LabReportsPage";
import DealerLocatorPage from "./pages/DealerLocatorPage";
import GalleryPage from "./pages/GalleryPage";
import QuizPage from "./pages/QuizPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import LegalDisclaimerPage from "./pages/LegalDisclaimerPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import NotFoundPage from "./pages/NotFoundPage";
import CookieConsent from "./components/CookieConsent";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import LoadingScreen from "./components/LoadingScreen";
import EasterEggManager from "./components/EasterEggManager";

// Analytics
import { initGA4, initMetaPixel, initClarity } from "./utils/analytics";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Redirect trailing-slash URLs to non-trailing-slash.
 * e.g. /products/ → /products (prevents duplicate indexing)
 */
const TrailingSlashRedirect = () => {
  const location = useLocation();
  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    return <Navigate to={location.pathname.slice(0, -1) + location.search + location.hash} replace />;
  }
  return null;
};

function AppContent() {
  const location = useLocation();
  
  return (
    <>
      <TrailingSlashRedirect />
      <ScrollToTop />
      <Navbar />
      <AnimatePresence mode="wait">
        <main className="min-h-screen" key={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:productId" element={<ProductDetailPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/distributors" element={<DistributorsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/calculator" element={<CalculatorPage />} />
            <Route path="/dealer-form" element={<DealerFormPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:articleId" element={<BlogDetailPage />} />
            <Route path="/strength-scanner" element={<StrengthScannerPage />} />
            <Route path="/savings-calculator" element={<SavingsCalculatorPage />} />
            <Route path="/tools" element={<ToolsHubPage />} />
            <Route path="/dealer-earnings" element={<DealerEarningsPage />} />
            <Route path="/dealer-status" element={<DealerStatusPage />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/bulk-order" element={<BulkOrderPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/lab-reports" element={<LabReportsPage />} />
            <Route path="/dealer-locator" element={<DealerLocatorPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal-disclaimer" element={<LegalDisclaimerPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <CookieConsent />
        </main>
      </AnimatePresence>
      <Footer />
      <WhatsAppButton />
      <EasterEggManager />
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#111113',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize analytics
    initGA4();
    initMetaPixel();
    initClarity();

    // Loading screen timer
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2200);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App bg-void">
      <HelmetProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
